<template>
	<AppDialog
		height="685"
		closeTop="-30"
		max-width="630"
		closeRight="-2"
		disable-max-width
		ref="dialog_container"
		:dialogName="'InboxDialog'"
	>
		<div class="dialog-content px-9 pt-12 pb-7">
			<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId">
				<template #default="{ indicator, isUpgradeRequired }">
					<Row>
						<h1 class="bold">{{ title }}</h1>
						<component v-if="isUpgradeRequired" :is="indicator" class="ml-2 mt-1" :billingFeatureId="billingFeatureId" />
					</Row>
				</template>
			</MaybeUpgradeRequiredIndicator>

			<InboxForm
				@submit="submit"
				:canEdit="canEdit"
				:isLoading="isLoading"
				:submitText="submitText"
				:editInboxName="editInbox?.name"
				:editInboxUsers="editInboxUsers"
				:editInboxIconName="editInbox?.icon_name"
				:editInboxGroups="editInbox?.groups || []"
				:editInboxChannelConfigs="editInbox?.channel_configs"
			/>
		</div>
	</AppDialog>
</template>

<script>
import { mapGetters } from 'vuex';

import InboxService from '../Inbox/inbox-service';
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog';
import InboxForm from '@/components/dialogs/InboxForm.vue';
import { createInbox, partialUpdateInbox } from '@/store/modules/inboxes/actions';
import { useInbox } from '@/store/modules/inboxes/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import ErrorHandlingService from '@/utils/error-handling-service';
export default {
	name: 'InboxDialog',
	components: {
		AppDialog,
		InboxForm,
		MaybeUpgradeRequiredIndicator,
	},
	props: {
		editInboxId: { type: [Number, String] },
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		...mapGetters(['user']),
		...mapGettersShorthand({ useInbox }),
		billingFeatureId() {
			return FeatureTypes.SharedNotificationInboxes;
		},
		title() {
			return this.isEditMode ? 'Edit inbox' : 'New shared inbox';
		},
		isEditMode() {
			return Boolean(this.editInboxId);
		},
		editInbox() {
			if (this.isEditMode) {
				return this.useInbox(this.editInboxId);
			}
			return null;
		},
		submitText() {
			return this.isEditMode ? 'Update' : 'Create';
		},
		editInboxUsers() {
			if (this.isEditMode) {
				return this.editInbox?.users || [];
			} else {
				return [this.user.id];
			}
		},
		canEdit() {
			return !this.isEditMode || InboxService.hasPermissionToEditInbox({ inbox: this.editInbox, userId: this.user.id });
		},
	},
	methods: {
		...mapActionsShorthand({ createInbox, partialUpdateInbox }),
		buildSuccessMsg(inboxName) {
			return `${inboxName} ${this.isEditMode ? 'updated' : 'created'} successfully`;
		},
		async submit(fields) {
			this.isLoading = true;
			try {
				const inbox = await (this.isEditMode ? this.partialUpdateInbox({ id: this.editInboxId, fields }) : this.createInbox(fields));
				this.$toast.success(this.buildSuccessMsg(inbox.name));
				this.dialogPromiseHandlers.resolve({ value: inbox.id, label: inbox.name });
				this.$refs.dialog_container.closeCurrentDialog();
			} catch (e) {
				const maybeMsg = ErrorHandlingService.extractErrorMessage(e) || 'Error occurred';
				if (maybeMsg) {
					this.$toast.error(maybeMsg);
				} else {
					throw e;
				}
			} finally {
				this.isLoading = false;
			}
		},
	},
	data() {
		return {
			isLoading: false,
		};
	},
};
</script>

<style scoped lang="scss">
small {
	color: gray;
}
.primary-button {
	min-width: 215px !important;
}
::v-deep .app-combobox .v-select__slot .v-select__selections {
	padding-right: 5px !important;
}
.dialog-content {
	width: 630px;
	min-height: 685px;
}
</style>
